import React, { useEffect, useRef } from "react";
import { NavigationContainer } from "@react-navigation/native";
import { QueryClientProvider } from "@tanstack/react-query";
import { I18nextProvider } from "react-i18next";
import { GestureHandlerRootView } from "react-native-gesture-handler";
import * as Font from "expo-font";
import { SafeAreaProvider } from "react-native-safe-area-context";
import { StatusBar } from "expo-status-bar";
import { AppState, AppStateStatus, Platform } from "react-native";

import ThemeProvider from "./ThemeProvider";
import i18n from "./localization/i18n";
import AppLoader from "./components/AppLoader";
import { useAppLoaderStore } from "./store/appLoaderStore";
import { queryClient } from "./services/tanStackClient";
import TabNavigator from "./routes/TabNavigator";
import { ExtendedPostHogProvider } from "./analytics/providers";
import { setupGlobalErrorHandlers } from "./ErrorHandler";
import { AudioProvider } from "./providers/AudioProvider";
import { PlatformProvider } from "./providers/PlatformProvider";
import { navRef, useNavStore } from "./store/navStore";
import useDeepLinks from "./hooks/useDeepLinks";
import { ActiveArticleProvider } from "./providers/ActiveArticleProvider";
import { ArticlesDataProvider } from "./providers/ArticlesDataProvider";
import ProductTourProvider from "./providers/ProductTourProvider";
import { LanguageSync } from "./localization/LanguageSync";
import { trackNavigation, trackAppForeground } from "./analytics/components";
import {
  trackAppInstalled,
  trackIdentify,
  trackReset,
  trackSessionStart,
} from "./analytics/analyticsUtil";
import { useUserStore } from "./store/userStore";
import { useTourStore } from "./store/useTourStore";
import CustomLinearGradient, {
  GRADIENT_TYPE,
} from "./components/CustomLinearGradient";
import {
  bootstrapAuth,
  initAuthListeners,
} from "./services/authService/authService";

import { useFcmLifecycle } from "./hooks/useFcmLifecycle";
import { useInstallSourceStore } from "./store/installSourceStore";
import usePlayInstallReferrer from "./hooks/usePlayInstallReferrer";
import InstallAppPrompt from "./features/share/components/InstallAppPrompt";

setupGlobalErrorHandlers();

export default function App() {
  useFcmLifecycle();
  usePlayInstallReferrer();
  const [fontsLoaded, fontError] = Font.useFonts({
    Poppins: require("../src/assets/fonts/PoppinsRegular.ttf"),
    "Poppins-Medium": require("../src/assets/fonts/PoppinsMedium.ttf"),
    "Poppins-Bold": require("../src/assets/fonts/PoppinsBold.ttf"),
  });

  const { loading, setLoading } = useAppLoaderStore();
  const initialized = useUserStore((s) => s.initialized);
  const currentUser = useUserStore((s) => s.user);
  const installTrackedRef = useRef(false);

  const onboardingCompleted = useTourStore((s) => !s.shouldShowOnboarding());
  useDeepLinks(onboardingCompleted);

  // ✅ Auth bootstrap
  useEffect(() => {
    bootstrapAuth();
    const unsub = initAuthListeners();
    return () => unsub?.();
  }, []);

  // Hide loader only when fonts + auth ready
  useEffect(() => {
    if(fontError) {
      console.log('fontError', fontError);
    }
    if (fontsLoaded && initialized) {
      setLoading(false);
    }
  }, [fontsLoaded, initialized, fontError]);

  // Track first install
  useEffect(() => {
    if (installTrackedRef.current) return;
    if (!currentUser?.firstTimeLogin) return;

    const installSource =
      useInstallSourceStore.getState().installSource || "organic";
    trackAppInstalled({
      user_uid: currentUser?.thinkoUser?.uid,
      firebase_uid: currentUser?.firebaseUser?.uid,
      platform: Platform.OS,
      first_time_login: true,
      install_source: installSource,
    });
    installTrackedRef.current = true;
  }, [currentUser]);

  // Identify user in PostHog so distinct_id resolves to a real user
  useEffect(() => {
    const uid = currentUser?.thinkoUser?.uid;
    if (uid) {
      trackIdentify(uid);
    } else if (initialized && !currentUser) {
      trackReset();
    }
  }, [currentUser?.thinkoUser?.uid, initialized]);

  // Cold-launch session
  useEffect(() => {
    if (!initialized) return;
    trackSessionStart();
  }, [initialized]);

  // Background / foreground session lifecycle
  useEffect(() => {
    const sub = AppState.addEventListener("change", (next: AppStateStatus) => {
      if (next === "active") {
        trackAppForeground(navRef);
        trackSessionStart();
      }
    });
    return () => sub.remove();
  }, []);

  if (!initialized) return null;
  return (
    <GestureHandlerRootView style={{ flex: 1 }}>
      <SafeAreaProvider>
        <NavigationContainer
          ref={navRef}
          onReady={() => {
            const route = navRef.getCurrentRoute();
            if (route) {
              useNavStore
                .getState()
                .setHistory([{ name: route.name, params: route.params }]);
            }
            trackNavigation(navRef);
          }}
          onStateChange={() => {
            const route = navRef?.getCurrentRoute();
            if (route) {
              useNavStore.getState().pushRoute(route);
            }
          }}
          linking={Platform.OS === 'web' ? { prefixes: [] } : undefined}
        documentTitle={{
          enabled: true,
          formatter: (options, route) => {
            const title = options?.title ?? route?.name;

            return title
              ? `thinkO News - ${title}`
              : 'thinkO News';
          },
        }}
        >
          <I18nextProvider i18n={i18n}>
            <LanguageSync />
            <QueryClientProvider client={queryClient}>
              <ArticlesDataProvider>
                <ActiveArticleProvider>
                  <ThemeProvider>
                    <AudioProvider>
                      <PlatformProvider>
                        <ExtendedPostHogProvider>
                          {loading ? (
                            <CustomLinearGradient
                              gradientType={GRADIENT_TYPE.BASE}
                            >
                              <AppLoader
                                isLoading
                                title="Curated by AI Personalised for you"
                                description="From dots to direction"
                              />
                            </CustomLinearGradient>
                          ) : (
                            <>
                              <StatusBar translucent />
                              <ProductTourProvider>
                                <TabNavigator />
                              </ProductTourProvider>
                              <InstallAppPrompt />
                            </>
                          )}
                        </ExtendedPostHogProvider>
                      </PlatformProvider>
                    </AudioProvider>
                  </ThemeProvider>
                </ActiveArticleProvider>
              </ArticlesDataProvider>
            </QueryClientProvider>
          </I18nextProvider>
        </NavigationContainer>
      </SafeAreaProvider>
    </GestureHandlerRootView>
  );
}